import { logger } from "@scott/logger";
import type { createScottApiClient } from "@scott/scott-api";
import { getScottApiClient } from "../scott-api/get-scott-api-client";
import {
  type CatalogueRequestKind,
  isCatalogueRequestStatsActive,
  recordCatalogueRequest,
} from "./catalogue-request-stats";

type ScottApiClient = ReturnType<typeof createScottApiClient>;

const HTTP_UNAUTHORIZED = 401;

/**
 * Runs a catalogue call as the current shopper, falling back to the shared
 * anonymous client if SAP rejects the shopper's token.
 *
 * A shopper's OCC token can expire while their CDC session is still valid, and
 * the session stores no expiry for it, so freshness cannot be checked up front.
 * Without this fallback SAP answers 401, the route surfaces an error and the
 * page 404s. Falling back costs that request its personalised pricing, which is
 * far better than losing the page entirely.
 */
export async function runCatalogueRequest<
  T extends { response?: { status?: number } },
>(
  context: { scottClient: ScottApiClient; isPersonalised: boolean },
  run: (client: ScottApiClient) => Promise<T>,
  kind: CatalogueRequestKind
): Promise<T> {
  const result = await timed(kind, () => run(context.scottClient));

  if (
    !context.isPersonalised ||
    result.response?.status !== HTTP_UNAUTHORIZED
  ) {
    return result;
  }

  logger.warn(
    "runCatalogueRequest: shopper token rejected by SAP, retrying anonymously"
  );

  const anonymousClient = await getScottApiClient();
  return await timed(kind, () => run(anonymousClient));
}

/** Runs one request and, when request stats are on, notes what it cost. */
async function timed<T extends { response?: { status?: number } }>(
  kind: CatalogueRequestKind,
  request: () => Promise<T>
): Promise<T> {
  if (!isCatalogueRequestStatsActive()) {
    return await request();
  }

  const startedAt = performance.now();
  try {
    const result = await request();
    recordCatalogueRequest(
      kind,
      performance.now() - startedAt,
      (result as { error?: unknown }).error !== undefined
    );
    return result;
  } catch (error) {
    recordCatalogueRequest(kind, performance.now() - startedAt, true);
    throw error;
  }
}
